import { appInjector } from '../../app-injector';
import { FourOhFourDataService } from '../../core-services/404.dataservice';
import { notificationOnError } from '../../core-services/Notifications.service';
import { DataConnector, HasId, Update } from './DataConnector';

export class DataStore<T extends HasId, U extends Update<T>, V extends T = T> {
    private cacheCollection: { [key: string]: T | Promise<T> } = {};
    defaultId: string;

    constructor(private connector: DataConnector<T, U, V>) {}

    get(id: string = this.defaultId): T {
        const details = this.acquire(id);
        return details instanceof Promise ? undefined : details;
    }

    fetch(id: string = this.defaultId): Promise<T> {
        const details = this.acquire(id);
        return details instanceof Promise ? details : Promise.resolve(details);
    }

    async refetch(id: string = this.defaultId): Promise<T> {
        if (!id) {
            return;
        }
        return this.doActualFetch(id);
    }

    private acquire(id: string): T | Promise<T> {
        if (!id) {
            return;
        }
        if (!this.cacheCollection[id]) {
            // not having the 'await' here is crucial to behavior
            this.cacheCollection[id] = this.doActualFetch(id);
        }
        return this.cacheCollection[id];
    }

    // will we need a similar mechanism to get/fetch for this
    async hydrate(basic: T = this.get(), level = 1): Promise<V> {
        if (!this.connector.hydrate) {
            throw new Error(
                'You can not hydrate an object unless you have defined an enhancement method in the connector!'
            );
        }
        // if I am already enhanced return the enhanced version
        const newItem = await this.connector.hydrate(basic, level);
        this.cacheCollection[basic.id] = newItem;
        return newItem;
    }

    @notificationOnError()
    private async doActualFetch(id: string): Promise<T> {
        try {
            const item = await this.connector.get(id);
            this.cacheCollection[id] = item;
            return this.cacheCollection[id];
        } catch (response) {
            if (response.status === 404) {
                const fourOhFour = appInjector().get(FourOhFourDataService);
                fourOhFour.redirectTo404(window.location.pathname);
            }
            throw response;
        }
    }

    async create(update: U): Promise<T> {
        const newItem = await this.connector.post(update);
        this.cacheCollection[newItem.id] = newItem;
        return newItem;
    }

    // could build sub classes that don't have patch
    async update(update: U): Promise<T> {
        if (!update.originalObject?.id) {
            throw new Error(
                "Attempting to update when I don't have an original id!"
            );
        }
        await this.connector.patch(update);
        if (update.optimisticUpdate) {
            if (!update.originalObject) {
                throw new Error(
                    "Attempting to update optimistically when I don't have original!"
                );
            }
            const newItem = update.optimisticUpdate();
            this.cacheCollection[newItem.id] = newItem;
            return Promise.resolve(newItem);
        } else {
            return this.refetch(update.originalObject?.id);
        }
    }

    async remove(id: string = this.defaultId) {
        await this.connector.delete(id);
        delete this.cacheCollection[id];
    }

    // This is a hack method used because this object does not control access
    // to all jobs. For example, delete
    wipeCache() {
        this.cacheCollection = {};
    }
}
